import React from 'react';
import { useTranslator, useUrlLocalizer } from 'tradera/lang/translate';
import { formatNumberWithSeparators } from 'tradera/utils/format';
import { useDayJs } from 'tradera/hooks/use-day-js';
import { useSekPriceFormatter } from 'tradera/localization/use-sek-price-formatter';
import { useLocalizedPriceFormatterWithExchangeRate } from 'tradera/localization/use-localized-price-formatter';
import { classNames } from 'tradera/utils/class-names';
import type {
    BidHistoryApiResponse,
    BidHistoryBidApiDto
} from 'tradera/state/services/types/webapi-shopping-generated';
import { type PaymentApiDto } from 'tradera/state/services/types/webapi-browse-generated';
import { CurrencyRateType } from 'tradera/state/multi-currency/reducer';

type BidsHistoryTableProps = Pick<
    BidHistoryApiResponse,
    'bids' | 'auctionStartDate' | 'auctionOpeningBid'
> & {
    className?: string;
    currencyIsSEK: boolean;
    payment?: PaymentApiDto | null;
    isBuyer: boolean;
};

export const BidsHistoryTable = ({
    bids,
    auctionOpeningBid,
    auctionStartDate,
    className,
    currencyIsSEK,
    payment,
    isBuyer
}: BidsHistoryTableProps) => {
    const { t } = useTranslator();
    const dayjs = useDayJs();
    const sekPriceFormatter = useSekPriceFormatter();
    const localizedPriceFormatter = useLocalizedPriceFormatterWithExchangeRate(
        payment?.paymentMethodExchangeRate,
        isBuyer ? CurrencyRateType.Buyer : CurrencyRateType.Seller
    );

    const formatDateTime = React.useCallback(
        (datetime: string) => dayjs(datetime).format('YYYY-MM-DD HH:mm'),
        [dayjs]
    );

    const formatMoney = React.useCallback(
        (value: number) =>
            currencyIsSEK
                ? sekPriceFormatter(value)
                : localizedPriceFormatter(value),
        [currencyIsSEK, sekPriceFormatter, localizedPriceFormatter]
    );

    return (
        <table className={classNames('table-fixed', className)}>
            <thead>
                <tr className="border-bottom">
                    <th
                        className={getBidsTableColumnClass(1, true)}
                        style={{ width: '25%' }}>
                        {t('bidHistory_bidders')}
                    </th>
                    <th
                        className={getBidsTableColumnClass(2, true)}
                        style={{ width: '20%' }}>
                        {t('bidHistory_bids')}
                    </th>
                    <th
                        className={getBidsTableColumnClass(3, true)}
                        style={{ width: '35%' }}>
                        {t('bidHistory_event')}
                    </th>
                    <th
                        className={getBidsTableColumnClass(4, true)}
                        style={{ width: '20%' }}>
                        {t('bidHistory_date')}
                    </th>
                </tr>
            </thead>
            <tbody>
                {bids?.map((bid) => (
                    <BidRow
                        key={bid.bidId}
                        bid={bid}
                        formatDateTime={formatDateTime}
                        formatMoney={formatMoney}
                    />
                ))}
                <tr className="text-muted">
                    <td className={getBidsTableColumnClass(1)}>
                        {t('bidHistory_begins')}
                    </td>
                    <td
                        className={classNames(
                            'text-no-wrap',
                            getBidsTableColumnClass(2)
                        )}>
                        {(auctionOpeningBid ?? 0) > -1 &&
                            formatMoney(auctionOpeningBid)}
                    </td>
                    <td className={getBidsTableColumnClass(3)} />
                    <td
                        className={classNames(
                            'text-no-wrap',
                            getBidsTableColumnClass(4)
                        )}>
                        {auctionStartDate && formatDateTime(auctionStartDate)}
                    </td>
                </tr>
            </tbody>
        </table>
    );
};

type BidRowProps = {
    bid: BidHistoryBidApiDto;
    formatDateTime: (datetime: string) => string;
    formatMoney: (value: number) => string;
};

const BidRow = ({ bid, formatDateTime, formatMoney }: BidRowProps) => {
    const { toLocalizedUrl } = useUrlLocalizer();
    const { t } = useTranslator();

    if (bid.removed) {
        return (
            <tr className="font-italic text-muted border-bottom">
                <td className={getBidsTableColumnClass(1)}>
                    {t('bidHistory_bidRemoved')}
                </td>
                <td className={getBidsTableColumnClass(2)}>
                    {formatMoney(bid.bidAmount)}
                </td>
                <td className={getBidsTableColumnClass(3)}>
                    {t('bidHistory_removedByTradera')}
                </td>
            </tr>
        );
    }

    const bidReasonLabel = getBidReasonLabel(bid.bidEvent ?? '');

    return (
        <tr className="border-bottom">
            <td
                className={getBidsTableColumnClass(1)}
                style={{ maxWidth: '1rem' }}>
                <a
                    rel="nofollow"
                    className="link text-decoration-none text-inter-medium font-weight-semibold"
                    data-nav=""
                    href={toLocalizedUrl(
                        `/profile/feedback/${bid.memberId}/${bid.memberAliasNormalized}`
                    )}>
                    {bid.memberAlias} (
                    {formatNumberWithSeparators(bid.memberTotalRating)})
                    <br className="d-md-none" />
                    <span className="d-block d-md-none text-muted font-weight-normal text-inter-medium">
                        {bidReasonLabel ? t(bidReasonLabel) : ''}
                    </span>
                </a>
            </td>
            <td
                className={classNames(
                    'text-no-wrap',
                    getBidsTableColumnClass(2)
                )}>
                {formatMoney(bid.bidAmount)}
                <br className="d-md-none" />
                <span className="d-block d-md-none text-muted text-inter-light">
                    {formatDateTime(bid.displayTime)}
                </span>
            </td>
            <td className={getBidsTableColumnClass(3)}>
                {bidReasonLabel ? t(bidReasonLabel) : ''}
            </td>
            <td
                className={classNames(
                    'text-no-wrap',
                    getBidsTableColumnClass(4)
                )}>
                {formatDateTime(bid.displayTime)}
            </td>
        </tr>
    );
};

const getBidReasonLabel = (bidEvent: string) => {
    switch (bidEvent) {
        case 'ReservedPriceReachedBid':
            return 'bidHistory_reservationPriceMetBid';
        case 'PutMaxBidFirst':
            return 'bidHistory_putMaxBidFirst';
        case 'IncreasedByMaxBid':
            return 'bidHistory_leadingAutoBid';
        case 'MaxBidReached':
            return 'bidHistory_nonLeadingAutoBid';
        case 'NoEvent':
            return '';
        default:
            return '';
    }
};

const getBidsTableColumnClass = (column: number, isHeader = false) => {
    switch (column) {
        case 1:
            return [
                'align-top',
                'py-2',
                'pr-2',
                'text-hyphenate',
                isHeader
                    ? 'font-hansen font-weight-bold'
                    : 'font-weight-semibold text-inter-medium'
            ].join(' ');
        case 2:
            return [
                'text-right',
                'align-top',
                'p-2',
                'text-no-wrap',
                'font-weight-bold',
                isHeader
                    ? 'font-hansen'
                    : 'font-weight-semibold text-inter-heavy'
            ].join(' ');
        case 3:
            return [
                'p-2',
                'align-top',
                'd-none',
                'd-md-table-cell',
                isHeader
                    ? 'font-hansen font-weight-bold'
                    : 'font-weight-semibold text-muted text-inter-light'
            ].join(' ');
        case 4:
            return [
                'd-none',
                'd-md-table-cell',
                'text-right',
                'align-top',
                'py-2',
                'pl-2',
                isHeader
                    ? 'font-hansen font-weight-bold'
                    : 'font-weight-bold text-muted text-inter-light'
            ].join(' ');
    }
};
